/**
 * Invoices List Page
 *
 * Filter panel & chip behaviour are delegated to the shared
 * FilterPanelController (components/FilterPanelController.js); this
 * module wires page-specific pieces: the customer SearchPicker,
 * datepicker → change bridge, table sorting via hidden inputs, and
 * bulk-PDF selection. All data fetching is HTMX.
 *
 * Lifecycle: the page is wired through ``data-auto-init="invoicesList"``
 * on the root container (see templates/office/invoices/list.html).
 * ``autoInitInvoicesList`` is invoked by ``_runAutoInit`` in app.js on
 * every ``htmx:afterSwap`` of #main-content and on ``htmx:historyRestore``,
 * which covers hx-boost forward/back navigation without any inline
 * <script> in the template (CSP compliance).
 */

import { SearchPicker } from '../components/SearchPicker.js';
import FilterPanelController from '../components/FilterPanelController.js';
import {
  renderCustomerItem,
  isCustomerDisabled,
  PICKER_MODE_FILTER,
} from '../utils/ui/customer-picker.js';

let customerPicker = null;
let panelController = null;

// Per-page idempotency: delegated listeners for bulk-PDF selection
// are bound ONCE for the SPA's lifetime (they survive hx-boost swaps
// because they live on ``document`` / ``document.body``). Without
// this flag, re-running ``initInvoicesList()`` after a boosted
// navigation would stack an extra listener each time — the classic
// HTMX-swap-reentrancy bug called out in .cursor/rules/no-inline-javascript.mdc.
let bulkSelectionBound = false;
// Idempotency guard for the sort-headers click delegate (Phase 6.B).
let sortDelegateBound = false;

export function initInvoicesList(options = {}) {
  const { urls = {} } = options;

  initFilterPanel();
  initCustomerPicker(urls);
  initDatepickerBridge();
  initBulkSelection();
  bindSortDelegationOnce();
}

/**
 * Auto-init entry point. Reads configuration from data-* attrs on
 * the root element so the Django template stays script-free.
 */
export function autoInitInvoicesList() {
  const root = document.querySelector('[data-auto-init="invoicesList"]');
  if (!root) return;
  initInvoicesList({
    urls: {
      customerSearch: root.dataset.customerSearchUrl || '/office/api/customers/search/',
    },
  });
}

function initFilterPanel() {
  // Keep a singleton controller across hx-boost re-inits. The
  // controller itself is idempotent per DOM node; we ALSO call init()
  // on every call because the toggle/reset button nodes get replaced
  // by hx-boost.
  if (!panelController) {
    panelController = new FilterPanelController({
      formId: 'filterForm',
      defaultValues: { status: 'active' },
      virtualKeys: { date_range: ['date_from', 'date_to'] },
      pickers: [{ name: 'customer', picker: () => customerPicker }],
      outsideIgnoreSelectors: '#customer-search-results',
    });
  }
  panelController.init();
}

function initCustomerPicker(urls) {
  const searchInput = document.getElementById('customer-search');
  const hiddenInput = document.getElementById('customer-filter');
  const form = () => document.getElementById('filterForm');

  if (!searchInput || !hiddenInput) return;

  customerPicker = new SearchPicker({
    inputId: 'customer-search',
    hiddenInputId: 'customer-filter',
    dropdownId: 'customer-search-results',
    endpoint: (urls.customerSearch || '/office/api/customers/search/') + '?include=all',
    maxResults: 20,
    minChars: 0,
    renderItem: (item) => renderCustomerItem(item, { mode: PICKER_MODE_FILTER }),
    isItemDisabled: (item) => isCustomerDisabled(item, PICKER_MODE_FILTER),
    onSelect: () => form()?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true })),
    onClear: () => form()?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true })),
  });
}

// renderCustomerItem moved to ``frontend/src/utils/ui/customer-picker.js``.

/**
 * Bridge Flowbite datepicker changeDate → native change event for HTMX.
 * Re-applied on every init because the date inputs live inside the
 * swapped #main-content (hx-boost replaces them on navigation).
 */
function initDatepickerBridge() {
  document.querySelectorAll('#date_from, #date_to').forEach((input) => {
    if (!input || input._invDpBound) return;
    input._invDpBound = true; // property on node (not dataset) → does not serialize into HTMX history snapshots
    input.addEventListener('changeDate', () => {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

// ========================================================
// Delegated click — sort headers (Phase 6.B)
// ========================================================
//
// Bound ONCE per SPA lifetime via sortDelegateBound. Catches
// [data-invoices-sort="<col>"] clicks anywhere on the page and
// dispatches to sortTable(). Replaces 8 inline onclick="sortTable(...)"
// in templates/office/invoices/list.html.

function handleInvoicesSortClick(evt) {
  const t = evt.target;
  if (!t || typeof t.closest !== 'function') return;
  const sortBtn = t.closest('[data-invoices-sort]');
  if (!sortBtn) return;
  evt.preventDefault();
  const col = sortBtn.dataset.invoicesSort;
  if (col) sortTable(col);
}

function bindSortDelegationOnce() {
  if (sortDelegateBound) return;
  sortDelegateBound = true;
  document.body.addEventListener('click', handleInvoicesSortClick);
}

/**
 * Sort table by column — updates hidden inputs and submits the form via HTMX.
 * Called from delegated click on [data-invoices-sort="<col>"] markers
 * (Phase 6.B — no inline onclick= in the template).
 */
export function sortTable(column) {
  const sortInput = document.getElementById('hidden-sort');
  const orderInput = document.getElementById('hidden-order');
  const form = document.getElementById('filterForm');
  if (!sortInput || !orderInput || !form) return;

  const currentSort = sortInput.value || 'issue_date';
  const currentOrder = orderInput.value || 'desc';

  let newOrder;
  if (currentSort === column) {
    newOrder = currentOrder === 'asc' ? 'desc' : 'asc';
  } else {
    newOrder = ['issue_date', 'due_date', 'total', 'remaining'].includes(column) ? 'desc' : 'asc';
  }

  sortInput.value = column;
  orderInput.value = newOrder;
  form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
}

/**
 * Bulk checkbox selection for PDF download.
 *
 * Listeners are bound ONCE on document/document.body so they survive
 * hx-boost swaps AND the #list-results (inner) swap that happens on
 * every filter/pagination change. The ``updateState`` call after
 * each inner swap refreshes the button's disabled/count state to
 * match the new checkbox set — previously done from an inline
 * ``htmx:afterSettle`` handler in the template, which accumulated a
 * listener per navigation.
 */
export function initBulkSelection() {
  updateBulkState();

  if (bulkSelectionBound) return;
  bulkSelectionBound = true;

  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!t || typeof t.matches !== 'function') return;
    if (t.matches('#inv-select-all')) {
      const isChecked = t.checked;
      document.querySelectorAll('.inv-row-checkbox').forEach((cb) => { cb.checked = isChecked; });
      updateBulkState();
    } else if (t.matches('.inv-row-checkbox')) {
      updateBulkState();
    }
  });

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    const btn = t.closest('#bulk-pdf-btn');
    if (!btn || btn.disabled) return;

    const form = document.getElementById('bulk-pdf-form');
    const checked = document.querySelectorAll('.inv-row-checkbox:checked');
    if (!form || !checked.length) return;

    form.querySelectorAll('input[name="invoice_ids"]').forEach((el) => el.remove());
    checked.forEach((cb) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'invoice_ids';
      input.value = cb.value;
      form.appendChild(input);
    });
    form.submit();
  });

  // Inner #list-results swaps (filtering, pagination) don't re-run
  // initInvoicesList, so refresh the bulk-btn state on every settle.
  // Also reinit Flowbite widgets (tooltips on action icons, dropdowns
  // on action menus) inside the freshly-swapped rows — app.js only
  // reinits on #main-content afterSwap, not on inner swaps.
  document.body.addEventListener('htmx:afterSettle', (evt) => {
    const target = evt.target;
    const id = target && target.id;
    if (id !== 'list-results' && id !== 'main-content') return;
    updateBulkState();
    if (typeof window.reinitFlowbiteWidgets === 'function') {
      window.reinitFlowbiteWidgets(target);
    }
  });
}

function updateBulkState() {
  const btn = document.getElementById('bulk-pdf-btn');
  if (!btn) return;
  const countSpan = document.getElementById('bulk-pdf-count');
  const all = document.querySelectorAll('.inv-row-checkbox');
  const checked = document.querySelectorAll('.inv-row-checkbox:checked');
  const count = checked.length;

  btn.disabled = count === 0;
  btn.classList.toggle('opacity-50', count === 0);
  btn.classList.toggle('cursor-not-allowed', count === 0);
  btn.classList.toggle('cursor-pointer', count > 0);
  if (countSpan) countSpan.textContent = count > 0 ? ` (${count})` : '';

  const selectAll = document.getElementById('inv-select-all');
  if (selectAll) {
    selectAll.checked = all.length > 0 && count === all.length;
    selectAll.indeterminate = count > 0 && count < all.length;
  }
}

window.initInvoicesList = initInvoicesList;
window.sortTable = sortTable;
window.initBulkSelection = initBulkSelection;
